/**
 * Contexto de autenticação.
 *
 * Expõe a sessão e uma única função de permissão para todo o painel. Nenhum componente deve ler
 * claim de token nem comparar papel por string: a decisão vem da matriz do domínio, a mesma que a
 * API usa. Assim uma tela habilitada por engano ainda é recusada no servidor.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { pode, type Acao } from '@emaster/dominio';

import { configurarCognito } from './configuracao.ts';
import { obterSessao, sair as sairDoCognito, type Sessao } from './servico.ts';

export type EstadoAutenticacao = 'carregando' | 'autenticado' | 'anonimo';

export interface ContextoAutenticacao {
  readonly estado: EstadoAutenticacao;
  readonly sessao: Sessao | null;
  /** `true` quando o usuário está autenticado mas ainda não recebeu papel. */
  readonly aguardandoLiberacao: boolean;
  readonly recarregar: () => Promise<void>;
  readonly sair: () => Promise<void>;
  /** Decide uma ação pela matriz do domínio. Guarda de interface, não de segurança. */
  readonly pode: (acao: Acao) => boolean;
}

const Contexto = createContext<ContextoAutenticacao | null>(null);

export function ProvedorAutenticacao({ children }: { readonly children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoAutenticacao>('carregando');
  const [sessao, setSessao] = useState<Sessao | null>(null);

  const recarregar = useCallback(async () => {
    const atual = await obterSessao();
    setSessao(atual);
    setEstado(atual === null ? 'anonimo' : 'autenticado');
  }, []);

  useEffect(() => {
    configurarCognito();
    void recarregar();
  }, [recarregar]);

  const sair = useCallback(async () => {
    await sairDoCognito();
    setSessao(null);
    setEstado('anonimo');
  }, []);

  const valor = useMemo<ContextoAutenticacao>(
    () => ({
      estado,
      sessao,
      aguardandoLiberacao: sessao !== null && sessao.perfil === null,
      recarregar,
      sair,
      pode: (acao: Acao) => pode(sessao?.perfil ?? null, acao),
    }),
    [estado, sessao, recarregar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Acessa o contexto de autenticação.
 *
 * @throws quando usado fora do provedor, que é erro de programação e não deve falhar em silêncio.
 */
export function useAutenticacao(): ContextoAutenticacao {
  const ctx = useContext(Contexto);
  if (ctx === null) {
    throw new Error('useAutenticacao precisa estar dentro de <ProvedorAutenticacao>.');
  }
  return ctx;
}
